import { ScrollArea } from "@/components/ui/scroll-area";
import SEO from "@/components/SEO";
import Header from "@/components/Header";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEO
        title="Privacy Policy - Utah Whiskey Tracker"
        description="Privacy Policy for Utah Whiskey Tracker. Learn how we collect, use, and protect your personal information and data."
        canonical="https://utahwhiskeytracker.com/privacy-policy"
      />
      <Header />
      <main className="container mx-auto px-4 py-8 flex-grow">
        <div className="max-w-4xl mx-auto bg-white p-6 rounded-lg shadow-md">
          <h1 className="text-3xl font-bold text-whiskey-dark mb-6">Privacy Policy</h1>
          
          <ScrollArea className="h-[60vh]">
            <div className="pr-4 space-y-6">
              <section>
                <h2 className="text-xl font-semibold text-whiskey-dark mb-3">1. Introduction</h2>
                <p className="mb-3">
                  Welcome to Whiskey Tracker. We respect your privacy and are committed to protecting your personal data.
                  This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website and services.
                </p>
                <p>
                  We encourage you to read this Privacy Policy carefully to understand our practices regarding your personal data.
                  By accessing or using our services, you acknowledge that you have read and understood this Privacy Policy.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-whiskey-dark mb-3">2. Information We Collect</h2>
                <p className="mb-2">We may collect the following types of information:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Personal Information:</strong> Name, email address, phone number, and other information you provide when creating an account.</li>
                  <li><strong>Usage Data:</strong> Information about how you use our website and services.</li>
                  <li><strong>Device Information:</strong> Information about the device you use to access our website.</li>
                  <li><strong>Location Information:</strong> General location information derived from your IP address.</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-whiskey-dark mb-3">3. How We Use Your Information</h2>
                <p className="mb-2">We may use the information we collect for various purposes, including to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Provide, maintain, and improve our services</li>
                  <li>Process transactions and send related information</li>
                  <li>Send notifications, alerts, and updates about whiskey availability</li>
                  <li>Respond to your comments, questions, and requests</li>
                  <li>Monitor and analyze usage patterns and trends</li>
                  <li>Protect our services and users</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-whiskey-dark mb-3">4. Text Message Communications</h2>
                <p className="mb-3">
                  If you provide your mobile phone number, you agree to receive SMS messages related to whiskey availability 
                  alerts and account notifications. Standard message and data rates may apply.
                </p>
                <p>
                  You may opt out of receiving SMS messages at any time by updating your preferences in Account Settings
                  or by replying STOP to any of our SMS messages.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-whiskey-dark mb-3">5. Information Sharing</h2>
                <p className="mb-3">
                  We do not sell your personal information. We may share your information with:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Service providers who perform services on our behalf</li>
                  <li>Business partners with your consent</li>
                  <li>Legal authorities when required by law</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-whiskey-dark mb-3">6. Data Security</h2>
                <p>
                  We implement appropriate security measures to protect your personal information. However, 
                  no method of transmission over the Internet or electronic storage is 100% secure, and we 
                  cannot guarantee absolute security.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-whiskey-dark mb-3">7. Your Rights</h2>
                <p className="mb-2">Depending on your location, you may have certain rights regarding your personal information, including:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Access to your personal data</li>
                  <li>Correction of inaccurate data</li>
                  <li>Deletion of your data</li>
                  <li>Restriction of processing</li>
                  <li>Data portability</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-whiskey-dark mb-3">8. Changes to This Privacy Policy</h2>
                <p>
                  We may update our Privacy Policy from time to time. We will notify you of any changes by posting 
                  the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to review 
                  this Privacy Policy periodically for any changes.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-whiskey-dark mb-3">9. Contact Us</h2>
                <p>
                  If you have questions or concerns about this Privacy Policy or our practices, please contact us at support@whiskeytracker.com.
                </p>
              </section>

              <p className="text-sm text-gray-500 mt-6">Last Updated: May 23, 2025</p>
            </div>
          </ScrollArea>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
